import React, { lazy, Suspense, useEffect, useRef } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { AppProvider } from "./context/AppContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from 'sonner';
import { initNativePlugins, isNative } from './lib/capacitor-bridge';
import { initPerformanceMonitoring, reportApiTiming } from './lib/performance';
import axios from 'axios';

// ============ GLOBAL: Force no-cache on every axios request ============
axios.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
  config.headers['Pragma'] = 'no-cache';
  config.headers['Expires'] = '0';
  // Add timestamp to bust any CDN/proxy cache
  config.params = config.params || {};
  config.params['_t'] = Date.now();
  // Track request start for timing
  config._perfStart = performance.now();
  return config;
});

// ============ GLOBAL: Track API response timing ============
axios.interceptors.response.use(
  (response) => {
    if (response.config._perfStart) {
      const duration = performance.now() - response.config._perfStart;
      const url = new URL(response.config.url, window.location.origin);
      reportApiTiming(response.config.method?.toUpperCase(), url.pathname, duration, response.status);
    }
    return response;
  },
  (error) => {
    if (error.config?._perfStart) {
      const duration = performance.now() - error.config._perfStart;
      const url = new URL(error.config.url, window.location.origin);
      reportApiTiming(error.config.method?.toUpperCase(), url.pathname, duration, error.response?.status || 0);
    }
    return Promise.reject(error);
  }
);

// Critical route (loaded immediately)
import { HomePage } from "./pages/HomePage";

// Lazy-loaded routes (loaded on demand)
const ResultsPage = lazy(() => import("./pages/ResultsPage").then(m => ({ default: m.ResultsPage })));
const ScorePage = lazy(() => import("./pages/ScorePage").then(m => ({ default: m.ScorePage })));
const SaveAnalysisPage = lazy(() => import("./pages/SaveAnalysisPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PaymentReturnPage = lazy(() => import("./pages/PaymentReturnPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const AccountDeletionPage = lazy(() => import("./pages/AccountDeletionPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AdminScoringDebug = lazy(() => import("./pages/AdminScoringDebug"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const PublicReportPage = lazy(() => import("./pages/PublicReportPage").then(m => ({ default: m.PublicReportPage })));
const FoldersPage = lazy(() => import("./pages/FoldersPage").then(m => ({ default: m.FoldersPage })));
const FolderDetailPage = lazy(() => import("./pages/FoldersPage").then(m => ({ default: m.FolderDetailPage })));
const PerformanceDashboard = lazy(() => import("./pages/PerformanceDashboard"));
const GrowthDashboard = lazy(() => import("./pages/GrowthDashboard"));
const InvestorDashboard = lazy(() => import("./pages/InvestorDashboard"));
const AnalyticsAuditPage = lazy(() => import("./pages/AnalyticsAuditPage"));
const NetworkCheckPage = lazy(() => import("./pages/NetworkCheckPage"));
const IntentLandingPage = lazy(() => import("./pages/IntentLandingPage"));
const PourQuiPage = lazy(() => import("./pages/PourQuiPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
import { captureAcquisition } from "./lib/acquisition";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Onboarding } from "./components/Onboarding";

// Route guard: waits for auth check to finish before deciding (fixes F5 logout bug)
function RequireAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
}

const PageLoader = () => (
  <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
  </div>
);

// Inner component that has access to router for deep link navigation
function AppRoutes() {
  const navigate = useNavigate();
  const initDone = useRef(false);

  useEffect(() => {
    if (!initDone.current) {
      initDone.current = true;
      initNativePlugins((path) => navigate(path));
    }
  }, [navigate]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/verifiez" element={<HomePage />} />
        <Route path="/results" element={<ResultsPage />} />
        <Route path="/score" element={<ScorePage />} />
        <Route path="/save-analysis" element={<SaveAnalysisPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/dashboard" element={<RequireAuth><DashboardPage /></RequireAuth>} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/payment/return" element={<PaymentReturnPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/confidentialite" element={<PrivacyPage />} />
        <Route path="/suppression-compte" element={<AccountDeletionPage />} />
        <Route path="/admin" element={<RequireAuth><AdminPage /></RequireAuth>} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/r/:slug" element={<PublicReportPage />} />
        <Route path="/folders" element={<RequireAuth><FoldersPage /></RequireAuth>} />
        <Route path="/folders/:folderId" element={<RequireAuth><FolderDetailPage /></RequireAuth>} />
        <Route path="/admin/performance" element={<RequireAuth><PerformanceDashboard /></RequireAuth>} />
        <Route path="/admin/growth" element={<RequireAuth><GrowthDashboard /></RequireAuth>} />
        <Route path="/admin/analytics-audit" element={<RequireAuth><AnalyticsAuditPage /></RequireAuth>} />
        <Route path="/network-check" element={<NetworkCheckPage />} />
        <Route path="/admin/scoring-debug" element={<RequireAuth><AdminScoringDebug /></RequireAuth>} />
        <Route path="/investor" element={<RequireAuth><InvestorDashboard /></RequireAuth>} />
        <Route path="/pour-qui" element={<PourQuiPage />} />
        <Route path="/verifier-client" element={<IntentLandingPage />} />
        <Route path="/verifier-fournisseur" element={<IntentLandingPage />} />
        <Route path="/verifier-entrepreneur" element={<IntentLandingPage />} />
        <Route path="/avant-acompte" element={<IntentLandingPage />} />
        <Route path="/verifier-entreprise" element={<IntentLandingPage />} />
        <Route path="/comprendre-sante-financiere" element={<IntentLandingPage />} />
        <Route path="/curiosite-entreprise" element={<IntentLandingPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

// /save-analysis : flux documentaire naturel — le min-h-screen gonflerait le document
// au-delà du viewport réduit par le clavier (cause du saut WKWebView + zone blanche).
function AppShell() {
  const location = useLocation();
  const naturalFlow = location.pathname === '/save-analysis';
  return (
    <div className={`App bg-[#F8FAFC] ${naturalFlow ? '' : 'min-h-screen'}`}>
      <AppRoutes />
    </div>
  );
}

function App() {
  useEffect(() => {
    initPerformanceMonitoring();
    captureAcquisition();
  }, []);

  return (
    <LanguageProvider>
      <Onboarding />
      <AuthProvider>
        <AppProvider>
          <div>
            <BrowserRouter>
              <ErrorBoundary>
                <AppShell />
              </ErrorBoundary>
            </BrowserRouter>
            <Toaster
              position="top-center"
              toastOptions={{
                style: {
                  background: '#FFFFFF',
                  color: '#172033',
                  border: '1px solid #E2E8F0',
                },
              }}
            />
          </div>
        </AppProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
